import React, { useEffect, useState } from 'react';
import { Box, Container, Grid, Switch, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { OptionalService } from '../../types';
import { useAgencyBranding } from '../../branding';
import { GetPAXTotalsResponse, getCityNameCleaned } from '../../helpers';
import { OptionalServiceCardDesktop } from '../OptionalServiceCardDesktop';
import { useDispatch } from 'react-redux';
import { changeShowOptionalServiceInDestination } from '../../slice';
import useBreakpoints from '../../../common/hooks/useBreakpoints';
import { OptionalServiceCardMobile } from '../OptionalServiceCardMobile';
import HelpTooltip from '../HelpContent/HelpTooltip';

const useStyles = makeStyles((theme) => ({
	paper: {
		marginTop: theme.spacing(4),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'left',
	},

	table: {
		minWidth: 650,
	},
	optionalServiceCardContainer: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(3),
	},
	destinationContainer: {
		marginTop: theme.spacing(2),
	},
	utility: {
		color: theme.palette.secondary.main,
		fontWeight: 'bold',
	},
	showOptionals: {
		color: theme.palette.secondary.main,
		fontWeight: 'bold',
	},
}));

interface DestinationOptionalService {
	optionalServices: OptionalService[];
	cityName: string;
}

interface Props {
	destinations: DestinationOptionalService[];
	paxTotals: GetPAXTotalsResponse;
	isPreview: boolean;
	isExternal?: boolean;
	onPreviewOptionalTotalChange?: (totalUSD: number) => void;
}

export const DestinationOptionalServiceList = (props: Props): JSX.Element => {
	const classes = useStyles();
	const dispatch = useDispatch();

	const { destinations, paxTotals, isPreview, isExternal, onPreviewOptionalTotalChange } = props;
	const branding = useAgencyBranding(isExternal);
	const sectionTitleSx = {
		marginBottom: '10px',
		width: '100%',
		background: branding.headerGradient,
		paddingLeft: '18px',
		paddingRight: '18px',
		paddingTop: '10px',
		paddingBottom: '10px',
		borderRadius: '10px',
		fontSize: '1rem',
		fontWeight: 700,
		color: 'white',
		boxShadow: '0 8px 20px rgba(49, 25, 75, 0.16)',
	};
	// Los opcionales arrancan TODOS apagados: `show` es el flag del agente para
	// mostrarlos en la propuesta, no una preselección. Sumar cargos que el
	// cliente no eligió rompe la confianza — es él quien decide prenderlos.
	const [previewSelectedOptionals, setPreviewSelectedOptionals] = useState<Record<string, boolean>>({});
	const hasDestinationWithService = !isPreview
		? destinations.some((x) => x.optionalServices.length > 0)
		: destinations.some((x) => x.optionalServices.length > 0);
	const handleChangeOptionalService = (destinationIndex: number, optionalServiceIndex: number) => {
		dispatch(changeShowOptionalServiceInDestination({ destinationIndex, optionalServiceIndex }));
	};
	const canEvaluateShowDestination = (destination: DestinationOptionalService) =>
		!isPreview ? destination.optionalServices.length > 0 : destination.optionalServices.length > 0;
	const togglePreviewOptional = (key: string) => {
		setPreviewSelectedOptionals((prev) => ({ ...prev, [key]: !prev[key] }));
	};
	useEffect(() => {
		if (!isPreview || !onPreviewOptionalTotalChange) return;
		const totalUSD = destinations.reduce(
			(destinationSum, destination, destinationIndex) =>
				destinationSum +
				destination.optionalServices.reduce((serviceSum, optionalService, optionalServiceIndex) => {
					const key = `${destinationIndex}-${optionalService.id}-${optionalServiceIndex}`;
					if (!previewSelectedOptionals[key]) return serviceSum;
					return (
						serviceSum +
						(optionalService.adultPrice || 0) * paxTotals.adults +
						(optionalService.childPrice || 0) * paxTotals.child +
						(optionalService.infantPrice || 0) * paxTotals.infants
					);
				}, 0),
			0,
		);
		onPreviewOptionalTotalChange(totalUSD);
	}, [
		destinations,
		isPreview,
		onPreviewOptionalTotalChange,
		paxTotals.adults,
		paxTotals.child,
		paxTotals.infants,
		previewSelectedOptionals,
	]);
	const isMediumDown = useBreakpoints().mediumDown;
	return (
		<>
			{hasDestinationWithService && (
				<Container component="main" maxWidth="md">
					<div className={classes.paper}>
						<Grid container spacing={2}>
							<Grid item xs={!isPreview ? 10 : 12} sm={!isPreview ? 10 : 12}>
								<Typography component="h1" variant="h6" sx={sectionTitleSx}>
									Servicios y actividades opcionales:
									{!isPreview && (
										<HelpTooltip
											helpKey="optionalServices"
											placement="bottom-end"
											iconButtonProps={{ sx: { float: 'inline-end', background: 'white', padding: '0px' } }}
										/>
									)}
								</Typography>
							</Grid>
							{!isPreview && (
								<Grid item xs={2} sm={2}>
									<Typography component="span" variant="body2" className={classes.showOptionals}>
										Mostrar adicional
									</Typography>
								</Grid>
							)}
						</Grid>
						{isPreview && (
							<Typography
								variant="body2"
								color="text.secondary"
								fontWeight={500}
								sx={{
									px: 1,
									pb: 1,
									color: 'text.secondary',
									fontWeight: 500,
									borderLeft: '3px solid',
									borderColor: 'secondary.main',
									ml: 0.5,
									mb: 1,
								}}
							>
								Agregá actividades o servicios extra a tu viaje. Estos valores son opcionales y no están incluidos en el
								presupuesto base.
							</Typography>
						)}
						{destinations.map(
							(destination, indexDestination) =>
								canEvaluateShowDestination(destination) &&
								destinations.findIndex((x) => x.cityName == destination.cityName) == indexDestination && (
									<div
										key={'destination-optional-service-container-' + indexDestination + '-' + destination.cityName}
										className={classes.destinationContainer}
									>
										<Typography
											component="h2"
											variant="subtitle1"
											sx={{
												mb: 1,
												px: 0.5,
												fontWeight: 800,
												color: 'primary.main',
												fontSize: '0.95rem',
											}}
										>
											Opcionales en {getCityNameCleaned(destination.cityName)}
										</Typography>

										{destination.optionalServices.map((optionalService, indexOptionalService) => {
											const previewKey = `${indexDestination}-${optionalService.id}-${indexOptionalService}`;
											const isSelectedInPreview = Boolean(previewSelectedOptionals[previewKey]);
											return (
												<React.Fragment key={previewKey}>
													{isPreview && (
														<div
															key={'optional-service-container-preview ' + destination.cityName + optionalService.id}
															className={classes.optionalServiceCardContainer}
														>
															{!isMediumDown && (
																<OptionalServiceCardDesktop
																	key={
																		'optional-service-container-preview-desktop-' +
																		indexDestination +
																		'-' +
																		destination.cityName +
																		optionalService.id
																	}
																	optionalService={optionalService}
																	paxTotals={paxTotals}
																	selectable
																	selected={isSelectedInPreview}
																	onToggle={() => togglePreviewOptional(previewKey)}
																/>
															)}
															{isMediumDown && (
																<Box>
																	<OptionalServiceCardMobile
																		key={
																			'optional-service-container-preview-mobile-' +
																			indexDestination +
																			'-' +
																			destination.cityName +
																			optionalService.id
																		}
																		optionalService={optionalService}
																		paxTotals={paxTotals}
																		selectable
																		selected={isSelectedInPreview}
																		onToggle={() => togglePreviewOptional(previewKey)}
																	/>
																</Box>
															)}
														</div>
													)}

													{!isPreview && (
														<Grid
															key={'optional-service-container-no-preview ' + destination.cityName + optionalService.id}
															className={classes.optionalServiceCardContainer}
															container
															spacing={2}
														>
															<Grid
																item
																xs={11}
																sm={11}
																key={
																	'optional-service-container-no-preview-grid-' +
																	indexDestination +
																	'-' +
																	destination.cityName +
																	optionalService.id
																}
															>
																{!isMediumDown && (
																	<OptionalServiceCardDesktop
																		key={
																			'optional-service-container-no-preview-desktop-' +
																			indexDestination +
																			'-' +
																			destination.cityName +
																			optionalService.id
																		}
																		optionalService={optionalService}
																		paxTotals={paxTotals}
																	/>
																)}
																{isMediumDown && (
																	<OptionalServiceCardMobile
																		key={
																			'optional-service-container-no-preview-mobile-' +
																			indexDestination +
																			'-' +
																			destination.cityName +
																			optionalService.id
																		}
																		optionalService={optionalService}
																		paxTotals={paxTotals}
																	/>
																)}
															</Grid>
															<Grid
																item
																xs={1}
																sm={1}
																container
																direction="row"
																justifyContent="center"
																alignItems="center"
																key={
																	'optional-service-container-no-preview-grid-switch-' +
																	indexDestination +
																	'-' +
																	destination.cityName +
																	optionalService.id
																}
															>
																<Switch
																	name="showOptionals"
																	checked={optionalService.show}
																	onChange={() => handleChangeOptionalService(indexDestination, indexOptionalService)}
																/>
															</Grid>
														</Grid>
													)}
												</React.Fragment>
											);
										})}
									</div>
								),
						)}
					</div>
				</Container>
			)}
		</>
	);
};
